import React, {
    useRef,
    useState,
    useMemo,
    useCallback,
    forwardRef,
    useImperativeHandle,
    useEffect
} from 'react';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';
import { Box } from '@mui/material';

// The following imports are not used in the final logic but kept for context.
// import HorizontalLineDialog from './HorizontalLineDialog'; 
import { useChartOverlays } from '../context/ChartOverlayContext'; 
// import Draggable from 'react-draggable'; 

import { processChartData } from '../utils/chartDataProcessor';
import { DEFAULT_WATERMARK, watermarkForMarket } from '../utils/branding';
import { BASE_TEXT_SIZE } from '../utils/textAnnotationSizes';
import { useAuth } from '../context/AuthContext';

// --- THEME CONSTANTS ---
// Moved outside the component to prevent re-creation on every render
const lightThemeColors = {
    candlestickUp: '#22c55e',
    candlestickDown: '#ef4444',
    volumeUp: 'rgba(34, 197, 94, 0.4)',
    volumeDown: 'rgba(239, 68, 68, 0.4)',
    tooltipBackground: 'rgba(255, 255, 255, 0.9)',
    tooltipBorder: '#ccc',
    tooltipColor: '#333',
    backgroundColor: 'transparent',
    graphic: [{
        type: 'text',
        left: 'center',
        top: '35%',
        style: {
            // Not the live watermark: nothing reads theme.graphic — the chart builds its own
            // `graphic` from watermarkText/watermarkOverride. Kept as the theme's shape only.
            text: DEFAULT_WATERMARK,
            font: 'bold 250px sans-serif',
            fill: 'rgba(180,180,180,0.4)',
            textAlign: 'center',
            textVerticalAlign: 'middle',
        }
    }],
    axisLabelColor: '#6e7079',
    crosshairColor: '#999',
    primary: '#c23531',
    trendlineUp: '#000000',
    trendlineDown: '#ef4444',
};

const darkThemeColors = {
    candlestickUp: '#22c55e',
    candlestickDown: '#ef4444',
    volumeUp: 'rgba(34, 197, 94, 0.4)',
    volumeDown: 'rgba(239, 68, 68, 0.4)',
    tooltipBackground: 'rgba(25, 27, 31, 0.9)',
    tooltipBorder: '#333',
    tooltipColor: '#fff',
    gridColor: '#2c3440',
    axisLabelColor: '#ccc',
    crosshairColor: '#777',
    primary: '#00da3c',
    trendlineUp: '#000000',
    trendlineDown: '#ef4444',
};

// --- FIX: Moved computeRSI function out of useMemo to prevent scoping/syntax errors ---
export const computeRSI = (bars, period = 14) => {
    if (!bars || bars.length < period + 1) return [];

    const rsiArr = [];
    let gainSum = 0, lossSum = 0;

    // Calculate initial average gain/loss for the first 'period' bars
    for (let i = 1; i <= period; i++) {
        const diff = bars[i][1] - bars[i - 1][1];
        gainSum += diff > 0 ? diff : 0;
        lossSum += diff < 0 ? -diff : 0;
    }

    let avgGain = gainSum / period;
    let avgLoss = lossSum / period;

    // Fill initial nulls for the period where RSI is not yet calculated
    for (let i = 0; i < period; i++) {
        rsiArr.push(null);
    }

    // Calculate first RSI value and place it at the correct index
    let rs = avgLoss === 0 ? Number.POSITIVE_INFINITY : avgGain / avgLoss;
    rsiArr[period] = avgLoss === 0 ? 100 : 100 - (100 / (1 + rs));

    // Calculate subsequent RSI values using Wilder's smoothing
    for (let i = period + 1; i < bars.length; i++) {
        const diff = bars[i][1] - bars[i - 1][1];
        const gain = diff > 0 ? diff : 0;
        const loss = diff < 0 ? Math.abs(diff) : 0;
        const avgGainSm = (avgGain * (period - 1) + gain) / period;
        const avgLossSm = (avgLoss * (period - 1) + loss) / period;
        rs = avgLossSm === 0 ? Number.POSITIVE_INFINITY : avgGainSm / avgLossSm;
        rsiArr.push(avgLossSm === 0 ? 100 : 100 - (100 / (1 + rs)));
        avgGain = avgGainSm;
        avgLoss = avgLossSm;
    }
    return rsiArr;
}
// ------------------------------------------------------------------------------------


const findClosestTradingDayIndex = (targetDate, allDates) => {
    let searchRadius = 0;
    while (searchRadius < 10) { // Limit search to +/- 10 days
        let forwardDate = new Date(targetDate);
        forwardDate.setDate(forwardDate.getDate() + searchRadius);
        let forwardDateString = forwardDate.toISOString().split('T')[0];
        let index = allDates.indexOf(forwardDateString);
        if (index !== -1) return index;

        if (searchRadius > 0) {
            let backwardDate = new Date(targetDate);
            backwardDate.setDate(backwardDate.getDate() - searchRadius);
            let backwardDateString = backwardDate.toISOString().split('T')[0];
            index = allDates.indexOf(backwardDateString);
            if (index !== -1) return index;
        }
        searchRadius++;
    }
    return -1; // Not found within reasonable range
};

const EChartComponent = forwardRef((props, ref) => {
    // Destructure props.
    const { 
        showRSI = false, 
        rsiData: propRsiData = [],
        maConfigs,
        barData: propsBarData,
        theme,
        loading: propsLoading,
        chartType,
        viewMode,
        chartColors: propsChartColors,
        overlays,
        showMACD,
        macdData,
        setPatternDialogOpen,
        onBarSelect,
        barFloaterMode = 'ohlcv',
        watermarkOverride,
        watermarkText,
        onUpdateOverlay,
    } = props;

    const { api, user } = useAuth();

    /**
     * Brand for the watermark.
     *
     * Derived here from the signed-in user rather than relying only on the watermarkText prop.
     * The prop is still honoured — App passes it — but a watermark that silently falls back to the
     * wrong brand whenever one link in a prop chain is missed is not worth the coupling, and this
     * component already has the auth context to hand.
     */
    const resolvedWatermark =
        watermarkText || watermarkForMarket(user?.marketId ?? user?.market);

    // ── Bar Floater state ──────────────────────────────────────────────
    const [floaterData, setFloaterData] = useState(null);
    const [floaterPos, setFloaterPos] = useState({ x: 0, y: 0, visible: false });
    const floaterCacheRef = useRef({});  // { 'yyyy-MM-dd': { planets: [...], aspects: [...] } }
    const floaterModeRef = useRef(barFloaterMode);
    useEffect(() => { floaterModeRef.current = barFloaterMode; }, [barFloaterMode]);
    const floaterDateRef = useRef(null);
    const floaterFetchingRef = useRef(null);
    const fetchFloaterRef = useRef(null);

    // Clear cache when mode changes since different modes return different data
    useEffect(() => { floaterCacheRef.current = {}; }, [barFloaterMode]);

    const fetchFloaterData = useCallback(async (dateStr) => {
        const curMode = floaterModeRef.current;
        if (!dateStr || curMode === 'ohlcv') return;
        const cacheKey = `${curMode}:${dateStr}`;
        if (floaterCacheRef.current[cacheKey]) {
            setFloaterData(floaterCacheRef.current[cacheKey]);
            return;
        }
        if (floaterFetchingRef.current === cacheKey) return;
        floaterFetchingRef.current = cacheKey;
        try {
            const isAspects = curMode.startsWith('aspects');
            const mode = isAspects ? 'aspects' : 'planets';
            const coordSystem = curMode === 'aspects_helio' ? 'helio'
                              : curMode === 'planets_trop'  ? 'tropical'
                              : curMode === 'planets_sid'   ? 'sidereal'
                              : 'geo';
            const resp = await api.get(`/api/bar-floater-data?date=${dateStr}&mode=${mode}&coordSystem=${coordSystem}`);
            floaterCacheRef.current[cacheKey] = resp.data;
            if (floaterDateRef.current === dateStr) setFloaterData(resp.data);
        } catch (e) {
            console.warn('[Floater] fetch error', e);
        } finally {
            if (floaterFetchingRef.current === cacheKey) floaterFetchingRef.current = null;
        }
    }, [api]);
    useEffect(() => { fetchFloaterRef.current = fetchFloaterData; }, [fetchFloaterData]);

    useChartOverlays();

    // --- Zoom Persistence ---
    const zoomRef = useRef({ startValue: null, endValue: null });
    const astroSeriesRef = useRef(null);  // full AstroGraph numeric array for dynamic rescaling on zoom
    // Adding a drawing overlay should NOT reset the zoom.
    const prevOverlayCountRef = useRef((overlays || []).length);
    useEffect(() => {
        const count = (overlays || []).length;
        if (count === 0 && prevOverlayCountRef.current > 0) {
            zoomRef.current = { startValue: null, endValue: null };
        }
        prevOverlayCountRef.current = count;
    }, [overlays]);

    // Track the last three clicked bar indices
    const [showCycleMarkers, setShowCycleMarkers] = useState(false);
    const [showPatterns] = useState(false); 
    
    // --- REFS AND LOCAL STATE ---
    const chartRef = useRef(null);

    const [dividerPercent] = useState(80);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
        setShowCycleMarkers,
        toggleCycleMarkers: () => setShowCycleMarkers((prev) => !prev),
        getShowCycleMarkers: () => showCycleMarkers,
        setPatternDialogOpen: setPatternDialogOpen,
        getEchartsInstance: () => chartRef.current.getEchartsInstance(), 
    }), [showCycleMarkers, setPatternDialogOpen]);

    // --- Stable click handler via native DOM (survives ECharts instance recreation) ---
    const chartWrapperRef = useRef(null);
    const onBarSelectRef = useRef(onBarSelect);
    useEffect(() => { onBarSelectRef.current = onBarSelect; }, [onBarSelect]);
    const barDatesRef = useRef(props.barData?.dates);
    useEffect(() => { barDatesRef.current = props.barData?.dates; }, [props.barData?.dates]);
    const mouseDownPos = useRef(null);
    useEffect(() => {
        const wrapper = chartWrapperRef.current;
        if (!wrapper) return;
        const onDown = (e) => { mouseDownPos.current = { x: e.clientX, y: e.clientY }; };
        const onClick = (e) => {
            // Ignore drags (scroll / zoom gestures) — only true clicks (< 5px movement)
            if (mouseDownPos.current) {
                const dx = e.clientX - mouseDownPos.current.x;
                const dy = e.clientY - mouseDownPos.current.y;
                if (dx * dx + dy * dy > 25) return; // 5px threshold
            }
            const cb = onBarSelectRef.current;
            if (!cb) return;
            const instance = chartRef.current?.getEchartsInstance?.();
            if (!instance) return;
            const rect = wrapper.getBoundingClientRect();
            const offsetX = e.clientX - rect.left;
            const offsetY = e.clientY - rect.top;
            if (!instance.containPixel({ gridIndex: 0 }, [offsetX, offsetY])) return;
            const pointInGrid = instance.convertFromPixel({ gridIndex: 0 }, [offsetX, offsetY]);
            if (pointInGrid) {
                const dataIndex = Math.round(pointInGrid[0]);
                const price = pointInGrid[1];
                const dates = barDatesRef.current;
                if (dates && dataIndex >= 0 && dataIndex < dates.length) {
                    cb(dates[dataIndex], dataIndex, price);
                }
            }
        };
        wrapper.addEventListener('mousedown', onDown);
        wrapper.addEventListener('click', onClick);
        return () => {
            wrapper.removeEventListener('mousedown', onDown);
            wrapper.removeEventListener('click', onClick);
        };
    }, []);

    // --- Generic Grid Click Handler ---


 

    // --- Chart Colors Memo ---
    const currentChartColors = useMemo(() => {
        const selectedThemeColors = theme.palette.mode === 'dark' ? darkThemeColors : lightThemeColors;
        return { ...selectedThemeColors, ...propsChartColors };
    }, [propsChartColors, theme.palette.mode]);

    // --- Study mode refs (needed by both the memo short-circuit and the patch effect) ---
    const studyModeRef = useRef(false);
    studyModeRef.current = !!props.studyMode;
    const barDataRef = useRef(propsBarData);
    barDataRef.current = propsBarData;
    // Mirrored into a ref because the study-reveal patch below depends only on propsBarData, on
    // purpose — adding chartType to its deps would make every view change run the reveal path.
    const chartTypeRef = useRef(chartType);
    chartTypeRef.current = chartType;
    const studyBarCountRef = useRef(0);

    // --- Final Chart Options Memo (CRITICAL LOGIC) ---
    const cachedChartOptionsRef = useRef(null);
    const finalChartOptions = useMemo(() => {
        // Short-circuit: during study mode, skip expensive recomputation.
        // The study reveal useEffect handles the cheap incremental patch separately.
        if (studyModeRef.current && cachedChartOptionsRef.current && studyBarCountRef.current > 0) {
            return cachedChartOptionsRef.current;
        }

        // --- 1. Calculate Initial Zoom/View Window ---
        const realBars = propsBarData?.realBarCount || propsBarData?.ohlc?.length || 0;
        const totalBars = propsBarData?.ohlc?.length || 0;
        const extensionBars = Math.max(0, totalBars - realBars);
        
        let initialStart = 0;
        let initialEnd = Math.min(totalBars - 1, realBars + 50 - 1);
        if (extensionBars >= 50) {
            initialEnd = realBars + 50 - 1;
        } else {
            initialEnd = totalBars - 1;
        }
        
        let zoomStart = null, zoomEnd = null;
        if (zoomRef.current.startValue !== null && zoomRef.current.endValue !== null) {
            zoomStart = zoomRef.current.startValue;
            zoomEnd = zoomRef.current.endValue;
        } else {
            zoomStart = initialStart;
            zoomEnd = initialEnd;
        }


        // --- 2. Prepare Vertical and Cycle MarkLines ---
        const cycleLineOverlays = (overlays || []).filter(ovr => ovr.type === 'cycleLines');
        const extraMarkLines = cycleLineOverlays.flatMap(cycleLinesConfig => {
            const lines = [];
            const bar1Index = propsBarData.dates.indexOf(cycleLinesConfig.bar1Date);
            const bar2Index = propsBarData.dates.indexOf(cycleLinesConfig.bar2Date);

            if (bar1Index !== -1 && bar2Index !== -1) {
                const barDiff = Math.abs(bar2Index - bar1Index);
                if (barDiff > 0) {
                    lines.push({ xAxis: bar1Index, lineStyle: { type: 'dotted', color: cycleLinesConfig.color || 'red', width: cycleLinesConfig.lineWidth || 1 } });
                    lines.push({ xAxis: bar2Index, lineStyle: { type: 'dotted', color: cycleLinesConfig.color || 'red', width: cycleLinesConfig.lineWidth || 1 } });

                    const ratio = cycleLinesConfig.displayType === 'fib_ratio_length' ? parseFloat(cycleLinesConfig.fibRatio) : 1;

                    if (cycleLinesConfig.distanceUnit === 'days') {
                        const date1 = new Date(cycleLinesConfig.bar1Date);
                        const date2 = new Date(cycleLinesConfig.bar2Date);
                        const dayDiff = Math.abs(Math.round((date2 - date1) / (1000 * 60 * 60 * 24)));
                        const projectedDayInterval = Math.round(dayDiff * ratio);

                        if (projectedDayInterval <= 0) return [];

                        const laterDate = date1 > date2 ? date1 : date2;
                        const earlierDate = date1 < date2 ? date1 : date2;

                        // Project forward from the later date
                        let lastFoundDateFwd = laterDate;
                        while (lastFoundDateFwd.getTime() < new Date(propsBarData.dates[propsBarData.dates.length - 1]).getTime()) {
                            const nextProjectedDate = new Date(lastFoundDateFwd);
                            nextProjectedDate.setDate(nextProjectedDate.getDate() + projectedDayInterval);
                            
                            const nextIndex = findClosestTradingDayIndex(nextProjectedDate, propsBarData.dates);

                            if (nextIndex !== -1 && new Date(propsBarData.dates[nextIndex]) > lastFoundDateFwd) {
                                lines.push({ xAxis: nextIndex, lineStyle: { type: 'solid', color: cycleLinesConfig.color || 'red', width: cycleLinesConfig.lineWidth || 1 } });
                                lastFoundDateFwd = new Date(propsBarData.dates[nextIndex]);
                            } else {
                                break;
                            }
                        }

                        // Project backward from the earlier date
                        let lastFoundDateBwd = earlierDate;
                        while (lastFoundDateBwd.getTime() > new Date(propsBarData.dates[0]).getTime()) {
                            const nextProjectedDate = new Date(lastFoundDateBwd);
                            nextProjectedDate.setDate(nextProjectedDate.getDate() - projectedDayInterval);

                            const nextIndex = findClosestTradingDayIndex(nextProjectedDate, propsBarData.dates);

                            if (nextIndex !== -1 && new Date(propsBarData.dates[nextIndex]) < lastFoundDateBwd) {
                                lines.push({ xAxis: nextIndex, lineStyle: { type: 'solid', color: cycleLinesConfig.color || 'red', width: cycleLinesConfig.lineWidth || 1 } });
                                lastFoundDateBwd = new Date(propsBarData.dates[nextIndex]);
                            } else {
                                break;
                            }
                        }
                    } else { // 'bars' mode
                        const projectedDiff = Math.round(barDiff * ratio);
                        if (projectedDiff <= 0) return []; // Prevent infinite loop

                        if (projectedDiff > 0) {
                            // Project lines to the right
                            for (let i = bar2Index + projectedDiff; i < propsBarData.dates.length; i += projectedDiff) {
                                lines.push({ xAxis: i, lineStyle: { type: 'solid', color: cycleLinesConfig.color || 'red', width: cycleLinesConfig.lineWidth || 1 } });
                            }

                            // Project lines to the left
                            for (let i = bar1Index - projectedDiff; i >= 0; i -= projectedDiff) {
                                lines.push({ xAxis: i, lineStyle: { type: 'solid', color: cycleLinesConfig.color || 'red', width: cycleLinesConfig.lineWidth || 1 } });
                            }
                        }
                    }
                }
            }
            return lines;
        });

        if (!propsBarData || !propsBarData.dates || propsBarData.dates.length === 0) {
            return {};
        }

        // horizontalLine overlays are now handled by chartDataProcessor.getMarkings,
        // so we only need cycle-line extraMarkLines here.

        // --- Determine Axis Visibility ---
        const hasDeclination = (overlays || []).some(o => o.type === 'planetaryLine' && o.mode === 'declination');
        const hasVolumeLongitude = (overlays || []).some(o => o.type === 'planetaryLine' && o.panel === 'volume');
        const hasAstroGraph = (overlays || []).some(o => o.type === 'astroGraphLine');
        // No explicit axis bounds — let ECharts auto-scale with scale:true.
        // Keep astroSeriesRef for diagnostics only.
        if (hasAstroGraph && propsBarData && propsBarData.dates) {
            const agOverlay = (overlays || []).find(o => o.type === 'astroGraphLine');
            if (agOverlay && Array.isArray(agOverlay.data)) {
                const valMap = {};
                agOverlay.data.forEach(pt => {
                    const v = pt.value;
                    if (typeof v === 'number' && isFinite(v)) {
                        valMap[(pt.date || '').substring(0, 10)] = v;
                    }
                });
                astroSeriesRef.current = propsBarData.dates.map(d => {
                    const v = valMap[(d || '').substring(0, 10)];
                    return (typeof v === 'number' && isFinite(v)) ? v : null;
                });
            }
        } else {
            astroSeriesRef.current = null;
        }
        const hasCharacteristicSpeed = (overlays || []).some(o => o.type === 'characteristicLine' && o.characteristicType === 'speed' && o.panel !== 'main');
        const hasCharacteristicDistPct = (overlays || []).some(o => o.type === 'characteristicLine' && (o.characteristicType === 'distance_percent' || o.characteristicType === 'distance_percent_diff') && o.panel !== 'main');
        const hasCharacteristicDiffDeg = (overlays || []).some(o => o.type === 'characteristicLine' && (o.characteristicType === 'longitude_diff' || o.characteristicType === 'declination_diff') && o.panel !== 'main');
        const hasCharacteristicSpeedMain = (overlays || []).some(o => o.type === 'characteristicLine' && o.characteristicType === 'speed' && o.panel === 'main');
        const hasCharacteristicDistPctMain = (overlays || []).some(o => o.type === 'characteristicLine' && (o.characteristicType === 'distance_percent' || o.characteristicType === 'distance_percent_diff') && o.panel === 'main');
        const hasCharacteristicDiffDegMain = (overlays || []).some(o => o.type === 'characteristicLine' && (o.characteristicType === 'longitude_diff' || o.characteristicType === 'declination_diff') && o.panel === 'main');

        // --- 4. Process Chart Data (Calling External Utility) ---
        const { 
            mainSeries, 
            maSeries, 
            trendLineSeries, 
            planetaryLineSeries, 
            volumeSeries, 
            allFormattedDates 
        } = processChartData(
            propsBarData,
            currentChartColors,
            overlays,
            viewMode,
            chartType,
            showPatterns,
            null, // patternVisibility removed
            maConfigs
        );

        // Merge cycle-line extraMarkLines into mainSeries markLine (preserve existing from processChartData)
        if (mainSeries && extraMarkLines.length > 0) {
            mainSeries.markLine = mainSeries.markLine || {};
            const existing = mainSeries.markLine.data || [];
            mainSeries.markLine.data = [...existing, ...extraMarkLines];
            mainSeries.markLine.symbol = 'none'; 
            mainSeries.markLine.silent = true; 
        }

        // --- 4b. AstroGraph Pattern Prediction Lines ---
        const patternOverlay = (overlays || []).find(o => o.type === 'astroPatternLines');
        if (patternOverlay && Array.isArray(patternOverlay.patterns) && mainSeries) {
            const patternMarkLines = [];
            const futureMarkPoints = [];
            for (const pat of patternOverlay.patterns) {
                const dateStr = (pat.date || '').substring(0, 10);
                const idx = propsBarData.dates.findIndex(d => d.substring(0, 10) === dateStr);
                if (idx === -1) continue;

                const isFuture = pat.region === 'future';
                const prediction = pat.prediction || '';
                const shape = pat.shape || '';

                const isBearish = shape === 'Peak' || shape === 'DoubleTop'
                    || shape === 'ZeroCrossDown' || shape === 'SlopeReversalDown'
                    || prediction === 'Bearish';
                const isBullish = shape === 'Trough' || shape === 'DoubleBottom'
                    || shape === 'ZeroCrossUp' || shape === 'SlopeReversalUp'
                    || prediction === 'Bullish';

                const color = isBearish ? '#e53935' : isBullish ? '#43a047' : '#9e9e9e';
                const arrow = isBearish ? '\u2193' : isBullish ? '\u2191' : '\u2013';
                const shortShape = shape.replace('SlopeReversal', 'Slope').replace('ZeroCross', 'ZX').replace('Double', 'Dbl');

                // Vertical line for both historical and future
                patternMarkLines.push({
                    xAxis: idx,
                    lineStyle: {
                        type: isFuture ? 'dashed' : 'solid',
                        color,
                        width: isFuture ? 2 : 1,
                    },
                    label: {
                        show: !isFuture,
                        formatter: `${shortShape} ${arrow}`,
                        position: 'start',
                        fontSize: 9,
                        color,
                        rotate: 90,
                        offset: [0, 4],
                    },
                });

                // Floating annotation for future predictions only
                if (isFuture) {
                    const hitRate = pat.hitRate != null ? Math.round(pat.hitRate) : null;
                    const avgChg = pat.avgChange10d != null ? pat.avgChange10d : null;
                    const offsetLbl = pat.bestOffsetLabel || '';
                    const flatLen = pat.flatLength || 1;
                    let floaterText = `${shortShape} ${arrow}`;
                    if (prediction) floaterText += `\n${prediction}`;
                    if (hitRate != null) floaterText += `\nHit: ${hitRate}%`;
                    if (avgChg != null) floaterText += `\nAvg 10d: ${avgChg > 0 ? '+' : ''}${avgChg}%`;
                    if (offsetLbl) floaterText += `\nTypical: ${offsetLbl}`;
                    if (flatLen > 1) floaterText += `\nFlat: ${flatLen} bars`;

                    // Place annotation near the top for bearish, near the bottom for bullish
                    const ohlc = propsBarData.ohlc;
                    const yVal = isBearish
                        ? (ohlc[idx] ? Math.max(ohlc[idx][3], ohlc[idx][0]) : 0)
                        : (ohlc[idx] ? Math.min(ohlc[idx][2], ohlc[idx][1]) : 0);

                    futureMarkPoints.push({
                        coord: [idx, yVal],
                        symbol: isBearish ? 'triangle' : 'triangle',
                        symbolRotate: isBearish ? 180 : 0,
                        symbolSize: 14,
                        itemStyle: { color },
                        label: {
                            show: true,
                            formatter: floaterText,
                            position: isBearish ? 'top' : 'bottom',
                            fontSize: 10,
                            fontWeight: 'bold',
                            color,
                            backgroundColor: 'rgba(255,255,255,0.85)',
                            borderColor: color,
                            borderWidth: 1,
                            borderRadius: 4,
                            padding: [4, 6],
                            lineHeight: 14,
                        },
                    });
                }
            }
            if (patternMarkLines.length > 0) {
                mainSeries.markLine = mainSeries.markLine || {};
                const existing = mainSeries.markLine.data || [];
                mainSeries.markLine.data = [...existing, ...patternMarkLines];
                mainSeries.markLine.symbol = 'none';
                mainSeries.markLine.silent = true;
            }
            if (futureMarkPoints.length > 0) {
                mainSeries.markPoint = mainSeries.markPoint || {};
                const existingPts = mainSeries.markPoint.data || [];
                mainSeries.markPoint.data = [...existingPts, ...futureMarkPoints];
                mainSeries.markPoint.animation = false;
            }
        }

        // --- 5. RSI Setup ---
        let computedRSI = [];
        
        if (showRSI) {
            // Logic to compute RSI locally if propRsiData is not available
            if (!Array.isArray(propRsiData) || propRsiData.length === 0) {
                // FIX: Call the function that was defined outside of the useMemo
                computedRSI = computeRSI(propsBarData.ohlc);
            } else {
                computedRSI = propRsiData;
            }
        }

        // Tag candlestick + volume with stable IDs for targeted study-mode patching
        if (mainSeries) mainSeries.id = '__candle__';
        if (volumeSeries) volumeSeries.id = '__volume__';

        // Only include valid, non-null series in allSeries
        let allSeries = [
            mainSeries, 
            ...(Array.isArray(maSeries) ? maSeries : [maSeries]), 
            ...trendLineSeries, 
            ...(Array.isArray(planetaryLineSeries) ? planetaryLineSeries : [planetaryLineSeries]), 
            volumeSeries
        ].filter(s => s && typeof s === 'object' && s.type);

        // Add RSI series if present
        if (showRSI && computedRSI.length > 0) {
            allSeries.push({
                name: 'RSI',
                type: 'line',
                data: computedRSI,
                xAxisIndex: 1,
                yAxisIndex: 1,
                smooth: true,
                symbol: 'none',
                lineStyle: { color: '#1976D2', width: 1 },
                showSymbol: false,
                z: 10,
                markLine: {
                    data: [
                        { yAxis: 30, lineStyle: { color: '#888', width: 1, type: 'dashed' }, label: { show: true, formatter: '30' } },
                        { yAxis: 50, lineStyle: { color: '#888', width: 1, type: 'dashed' }, label: { show: true, formatter: '50' } },
                        { yAxis: 70, lineStyle: { color: '#888', width: 1, type: 'dashed' }, label: { show: true, formatter: '70' } },
                    ],
                    symbol: 'none',
                    lineStyle: { color: '#888', width: 1, type: 'dashed' },
                    label: { show: true }
                }
            });
        }

        if (showMACD && macdData && macdData.macd && macdData.signal && macdData.histogram) {
            allSeries.push({
                name: 'MACD Histogram',
                type: 'bar',
                data: macdData.histogram.map(pt => pt.value),
                xAxisIndex: 1,
                yAxisIndex: 3, 
                itemStyle: { color: '#90caf9' },
                barWidth: 6,
                z: 8
            });
            allSeries.push({
                name: 'MACD',
                type: 'line',
                data: macdData.macd.map(pt => pt.value),
                xAxisIndex: 1,
                yAxisIndex: 3, 
                lineStyle: { color: '#388e3c', width: 1 },
                showSymbol: false,
                z: 9
            });
            allSeries.push({
                name: 'MACD Signal',
                type: 'line',
                data: macdData.signal.map(pt => pt.value),
                xAxisIndex: 1,
                yAxisIndex: 3, 
                lineStyle: { color: '#d32f2f', width: 1, type: 'dashed' },
                showSymbol: false,
                z: 9
            });
        }

        // Chart options object
        const result = {
            backgroundColor: 'transparent',
            graphic: watermarkOverride?.hidden ? [] : [{
                type: 'text',
                left: 'center',
                top: '35%',
                style: {
                    text: watermarkOverride?.text || resolvedWatermark || DEFAULT_WATERMARK,
                    font: `bold ${watermarkOverride?.fontSize || 250}px sans-serif`,
                    fill: 'rgba(180,180,180,0.4)',
                    textAlign: 'center',
                    textVerticalAlign: 'middle',
                }
            }],
            tooltip: {
                trigger: 'item',
                axisPointer: {
                    type: 'cross',
                    link: [{ xAxisIndex: 'all' }],
                    crossStyle: {
                        color: currentChartColors.crosshairColor
                    }
                },
                formatter: (params) => {
                    if (!params || !params.name) return '';
                    // For candlestick series, show OHLCV with weekday
                    if (params.seriesType === 'candlestick' && params.data && Array.isArray(params.data)) {
                        const dateStr = params.name;
                        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                        let weekday = '';
                        let d;
                        // Handle YYYY-MM-DD format
                        const isoDash = dateStr.split('-');
                        const slashParts = dateStr.split('/');
                        if (isoDash.length === 3 && isoDash[0].length === 4) {
                            d = new Date(parseInt(isoDash[0]), parseInt(isoDash[1]) - 1, parseInt(isoDash[2]));
                        } else if (slashParts.length === 3) {
                            d = new Date(parseInt(slashParts[2]), parseInt(slashParts[0]) - 1, parseInt(slashParts[1]));
                        }
                        if (d && !isNaN(d.getTime())) weekday = days[d.getDay()];
                        // params.data = [categoryIndex, open, close, low, high]
                        const open  = params.data[1];
                        const close = params.data[2];
                        const low   = params.data[3];
                        const high  = params.data[4];
                        const vol = propsBarData?.volumes?.[params.dataIndex];
                        const row = (label, val) => `<tr><td style="padding-right:8px">${label}:</td><td style="text-align:right">${Number(val).toFixed(2)}</td></tr>`;
                        return `<b>${weekday} ${dateStr}</b>`
                            + `<table style="border-collapse:collapse;margin-top:4px">`
                            + row('Open', open)
                            + row('High', high)
                            + row('Low', low)
                            + row('Close', close)
                            + (vol != null ? `<tr><td style="padding-right:8px">Volume:</td><td style="text-align:right">${Number(vol).toLocaleString()}</td></tr>` : '')
                            + `</table>`;
                    }
                    return '';
                },
            },
            grid: [
                { left: '4%', right: '30px', top: '5%', height: `${dividerPercent - 2.5}%` }, // Index 0: Main Chart
                { left: '4%', right: '30px', top: `${dividerPercent + 2.5}%`, height: `${95 - (dividerPercent + 2.5)}%` }, // Index 1: Unified Bottom Panel
            ],
            xAxis: [
                { type: 'category', data: allFormattedDates, gridIndex: 0, axisLine: { onZero: false }, splitLine: { show: false }, axisLabel: { show: false }, axisTick: { show: false }, boundaryGap: true }, // Index 0 (Main)
                { type: 'category', data: allFormattedDates, gridIndex: 1, axisLine: { onZero: false }, axisTick: { show: true }, splitLine: { show: false }, axisLabel: { show: true, color: currentChartColors.axisLabelColor }, boundaryGap: true }, // Index 1 (Bottom Panel)
            ],
            yAxis: [
                // 0: Main price panel
                { scale: true, gridIndex: 0, splitArea: { show: true, areaStyle: { color: ['rgba(250,250,250,0.05)', 'rgba(200,200,200,0.05)'] } }, splitLine: { show: true, lineStyle: { color: currentChartColors.gridColor } }, axisLabel: { color: currentChartColors.axisLabelColor, inside: false }, position: 'right' },
                // 1: RSI (right) for volume panel
                { min: 0, max: 100, scale: false, gridIndex: 1, splitNumber: 4, axisLabel: { show: true, color: currentChartColors.axisLabelColor, inside: false }, axisLine: { show: false }, axisTick: { show: false }, splitLine: { show: true, lineStyle: { color: '#ccc', type: 'dotted' } }, position: 'right', name: 'RSI' },
                // 2: Volume (left) for volume panel
                { type: 'value', scale: true, gridIndex: 1, axisLabel: { show: true, color: currentChartColors.axisLabelColor }, axisLine: { show: true }, axisTick: { show: true }, splitLine: { show: true, lineStyle: { color: '#ccc', type: 'solid' } }, position: 'left', name: 'Volume' },
                // 3: MACD yAxis (auto-scale, centered at 0) for volume panel
                { type: 'value', scale: true, gridIndex: 1, axisLabel: { show: true, color: '#388e3c' }, axisLine: { show: true }, axisTick: { show: true }, splitLine: { show: true, lineStyle: { color: '#bbb', type: 'dashed' } }, position: 'left', offset: 40, name: 'MACD', min: 'dataMin', max: 'dataMax', show: showMACD },
                // 4: Dedicated planetary longitude axis
                { type: 'value', min: 0, max: 360, position: 'right', offset: 60, show: hasVolumeLongitude, name: 'Longitude (°)', gridIndex: 1, splitLine: { show: false } },
                // 5: Dedicated Declination axis (Main Panel)
                { type: 'value', scale: true, position: 'right', offset: 60, show: hasDeclination, name: 'Declination', gridIndex: 0, splitLine: { show: false }, axisLabel: { formatter: '{value}°' } },
                // 6: AstroGraph daily score axis (Main Panel, left side) — auto-scaled
                { type: 'value', scale: true, position: 'left', offset: 40, show: hasAstroGraph, gridIndex: 0, splitLine: { show: false }, axisLabel: { color: '#ff6600', formatter: (v) => Math.round(v) } },
                // 7: Characteristics speed axis (Bottom Panel)
                { type: 'value', scale: true, gridIndex: 1, position: 'right', offset: 60, show: hasCharacteristicSpeed, name: 'Speed (°/day)', splitLine: { show: false } },
                // 8: Characteristics distance axis (Bottom Panel)
                { type: 'value', min: 0, max: 100, scale: false, gridIndex: 1, position: 'right', offset: 140, show: hasCharacteristicDistPct, name: 'Distance (%)', splitLine: { show: false } },
                // 9: Characteristics diff-deg axis (Bottom Panel)
                { type: 'value', min: 0, scale: true, gridIndex: 1, position: 'right', offset: 220, show: hasCharacteristicDiffDeg, name: 'Difference (°)', splitLine: { show: false } },
                // 10: Characteristics speed axis (Main Panel)
                { type: 'value', scale: true, gridIndex: 0, position: 'left', offset: 80, show: hasCharacteristicSpeedMain, name: 'Speed (°/day)', splitLine: { show: false }, axisLabel: { color: '#F44336' } },
                // 11: Characteristics distance axis (Main Panel)
                { type: 'value', min: 0, max: 100, scale: false, gridIndex: 0, position: 'left', offset: 120, show: hasCharacteristicDistPctMain, name: 'Distance (%)', splitLine: { show: false }, axisLabel: { color: '#4CAF50' } },
                // 12: Characteristics diff-deg axis (Main Panel)
                { type: 'value', min: 0, scale: true, gridIndex: 0, position: 'left', offset: 160, show: hasCharacteristicDiffDegMain, name: 'Difference (°)', splitLine: { show: false }, axisLabel: { color: '#2196F3' } },
            ],
            dataZoom: [
                { type: 'inside', xAxisIndex: [0, 1], filterMode: 'weakFilter', startValue: zoomStart, endValue: zoomEnd }, 
                { 
                    type: 'slider', 
                    xAxisIndex: [0, 1], 
                    filterMode: 'weakFilter',
                    startValue: zoomStart, 
                    endValue: zoomEnd, 
                    bottom: '0px', 
                    height: '20px', 
                    dataBackground: { lineStyle: { color: '#d3d3d3' }, areaStyle: { color: '#d3d3d3' } },
                    fillerColor: 'rgba(167,183,204,0.4)',
                    handleIcon: 'M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
                    handleSize: '80%',
                    handleStyle: { color: '#fff', shadowBlur: 3, shadowColor: 'rgba(0, 0, 0, 0.6)', shadowOffsetX: 2, shadowOffsetY: 2 },
                },
                { type: 'inside', yAxisIndex: [0], start: 0, end: 100 },
                { type: 'slider', yAxisIndex: [0], right: 0, width: 25, start: 0, end: 100 },
            ],
            toolbox: {
                show: true,
                top: 0,
                right: 20,
                feature: {
                    dataZoom: {
                        show: true,
                        title: { zoom: 'Area Zoom', back: 'Zoom Out' },
                        tooltip: 'Click this and then highlight the area of the chart you want to zoom in on. You can do the same with mouse wheel rolling forward (and zoom out with the wheel rolling backwards)'
                    },
                },
                itemSize: 15,
                itemGap: 5,
                emphasis: { iconStyle: { borderColor: '#007bff' } },
                iconStyle: { borderColor: '#6c757d', borderWidth: 1 },
                tooltip: {
                    show: false,
                    formatter(param) { return `<div>${param.title}</div>`; },
                    backgroundColor: '#555',
                    textStyle: { color: 'white' }
                }
            },
            series: allSeries,
        };
        cachedChartOptionsRef.current = result;
        return result;
    // NOTE: zoomWindow intentionally NOT a dependency — zoom is handled by ECharts natively.
    // zoomRef.current is read inside the memo when it runs for other reasons.
    //
    // showCycleMarkers was also listed here and is not read by this memo, so it only forced a full
    // rebuild that produced identical options. Nothing else reads that state either — the
    // toggleCycleMarkers action flips it and getShowCycleMarkers is never called — so the toggle
    // currently has no renderer behind it. Removing the dependency changes no output; whether the
    // feature should be restored or dropped is a separate question.
    }, [propsBarData, overlays, currentChartColors, viewMode, chartType, dividerPercent, showPatterns, showRSI, propRsiData, showMACD, macdData, maConfigs, watermarkOverride, resolvedWatermark]);

    // --- Stable onEvents: must NOT be recreated each render or echarts-for-react
    //     will dispose + recreate the entire ECharts instance (causes flicker). ---
    const stableOnEvents = useMemo(() => ({
        mousemove: (params) => {
            if (!params || !params.name) return;
            const rawDate = params.name;
            // Convert formatted date (e.g. "04/18/2025") back to yyyy-MM-dd
            let dateStr = rawDate;
            const parts = rawDate.split('/');
            if (parts.length === 3 && parts[0].length <= 2) {
                dateStr = `${parts[2]}-${parts[0].padStart(2,'0')}-${parts[1].padStart(2,'0')}`;
            }
            floaterDateRef.current = dateStr;
            if (floaterModeRef.current !== 'ohlcv') {
                // Use ECharts convertToPixel to pin the floater to the bar's x-coordinate
                let px = 0, py = 10;
                try {
                    const inst = chartRef.current?.getEchartsInstance?.();
                    if (inst && params.dataIndex != null) {
                        const pixel = inst.convertToPixel({ xAxisIndex: 0 }, params.dataIndex);
                        if (pixel != null) px = pixel + 16;
                    }
                } catch (_) { /* fallback */ }
                if (!px) {
                    // Fallback: use the native DOM event offset
                    const evt = params.event?.event || params.event;
                    px = (evt?.offsetX || 0) + 16;
                }
                setFloaterPos({ x: px, y: py, visible: true });
                // Trigger fetch (cached results return instantly)
                if (fetchFloaterRef.current) fetchFloaterRef.current(dateStr);
            }
        },
        globalout: () => {
            setFloaterPos(p => ({ ...p, visible: false }));
            floaterDateRef.current = null;
        },
        click: () => {
            setFloaterPos(p => ({ ...p, visible: false }));
            floaterDateRef.current = null;
        },
        datazoom: (params) => {
            if (params && params.batch && params.batch.length > 0) {
                const batch = params.batch[0];
                if (typeof batch.startValue !== 'undefined' && typeof batch.endValue !== 'undefined') {
                    zoomRef.current = {
                        startValue: batch.startValue,
                        endValue: batch.endValue,
                    };
                    // Only update the ref — do NOT call setZoomWindow here.
                    // ECharts handles zoom natively; calling setZoomWindow would
                    // trigger memo recomputation → notMerge:true full rebuild on
                    // every scroll, which corrupts the chart.
                }
            }
            // Auto-deactivate area zoom tool so cursor doesn't stay as "+"
            try {
                const inst = chartRef.current?.getEchartsInstance?.();
                if (inst) {
                    inst.dispatchAction({ type: 'takeGlobalCursor', key: 'dataZoomSelect', dataZoomSelectActive: false });
                }
            } catch (_) { /* ignore */ }
        },
    }), []); // eslint-disable-line react-hooks/exhaustive-deps

    const stableLoadingOption = useMemo(() => ({ text: 'Loading...' }), []);

    // --- We manage setOption ourselves: clear() + setOption() for full updates,
    //     ID-targeted patch for study bar reveals. ---
    const shouldSetOption = useMemo(() => () => false, []);

    // Study reveal: cheap incremental patch (avoids expensive finalChartOptions recomputation)
    useEffect(() => {
        if (!studyModeRef.current) {
            studyBarCountRef.current = 0;
            return;
        }
        const currentBarData = barDataRef.current;
        const barCount = currentBarData?.realBarCount || 0;
        if (studyBarCountRef.current > 0 && barCount > studyBarCountRef.current) {
            // Incremental study reveal — patch only price + volume data.
            //
            // The patch has to restate the series type, and it must be the type the current view
            // asks for. Hardcoding 'candlestick' here meant a study reveal silently converted a
            // line chart into candles mid-run; with the hidden view it would also have undone the
            // hiding, since a candlestick series carries none of the zero-opacity styling.
            const instance = chartRef.current?.getEchartsInstance?.();
            if (instance) {
                const priceSeries = chartTypeRef.current === 'candlestick'
                    ? { id: '__candle__', type: 'candlestick', xAxisIndex: 0, yAxisIndex: 0, data: currentBarData.ohlc, animation: false }
                    : {
                        id: '__candle__', type: 'line', xAxisIndex: 0, yAxisIndex: 0, animation: false,
                        data: currentBarData.ohlc.map(bar => (bar && bar[1] != null) ? bar[1] : null),
                        ...(chartTypeRef.current === 'hidden'
                            ? { lineStyle: { opacity: 0, width: 0 }, itemStyle: { opacity: 0 }, showSymbol: false, silent: true }
                            : {}),
                    };
                instance.setOption({
                    series: [
                        priceSeries,
                        { id: '__volume__', type: 'bar', xAxisIndex: 1, yAxisIndex: 2, data: currentBarData.volumes, animation: false },
                    ]
                }, false);
            }
        }
        studyBarCountRef.current = barCount;
    }, [propsBarData]);

    // Full chart update (runs when finalChartOptions actually changes — NOT on study reveals)
    useEffect(() => {
        const instance = chartRef.current?.getEchartsInstance?.();
        if (!instance) return;
        if (!finalChartOptions || Object.keys(finalChartOptions).length === 0) return;

        // Hide any active tooltip first — notMerge disposes old components,
        // and an in-flight tooltip would crash with "Cannot set innerHTML of null".
        try { instance.dispatchAction({ type: 'hideTip' }); } catch (_) { /* ignore */ }

        // Full update: notMerge replaces all components atomically — equivalent
        // to a fresh chart but without destroying DOM elements prematurely.
        instance.setOption(finalChartOptions, { notMerge: true });
    }, [finalChartOptions]);

    return (
        <Box ref={chartWrapperRef} sx={{ position: 'relative', width: '100%', height: '100%' }}>
            <ReactECharts
                ref={chartRef}
                option={finalChartOptions}
                shouldSetOption={shouldSetOption}
                style={{ height: '100%', width: '100%' }}
                onEvents={stableOnEvents}
                loadingOption={stableLoadingOption}
                showLoading={propsLoading || !propsBarData || propsBarData.ohlc.length === 0}
            />
            {/* Planetary Floater Overlay */}
            {barFloaterMode !== 'ohlcv' && floaterPos.visible && floaterData && !floaterData.error && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: floaterPos.x,
                        top: floaterPos.y,
                        zIndex: 2000,
                        bgcolor: 'rgba(255,255,255,0.96)',
                        border: '1px solid #bbb',
                        borderRadius: 1,
                        boxShadow: 2,
                        px: 1.5, py: 1,
                        pointerEvents: 'none',
                        fontSize: 12,
                        fontFamily: 'monospace',
                        maxWidth: 420,
                        whiteSpace: 'nowrap',
                    }}
                >
                    <Box sx={{ fontWeight: 'bold', mb: 0.5, fontSize: 13, color: '#1976d2' }}>
                        {floaterData.date}
                    </Box>
                    {(barFloaterMode === 'planets_sid' || barFloaterMode === 'planets_trop') && floaterData.planets && (
                        <table style={{ borderCollapse: 'collapse', fontSize: 11 }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid #ccc' }}>
                                    <th style={{ textAlign: 'left', padding: '1px 6px' }}>Planet</th>
                                    <th style={{ textAlign: 'right', padding: '1px 6px' }}>Long°</th>
                                    <th style={{ textAlign: 'left', padding: '1px 6px' }}>Sign</th>
                                    <th style={{ textAlign: 'right', padding: '1px 6px' }}>Decl°</th>
                                    <th style={{ textAlign: 'right', padding: '1px 6px' }}>Dist(AU)</th>
                                    <th style={{ textAlign: 'right', padding: '1px 6px' }}>Speed</th>
                                </tr>
                            </thead>
                            <tbody>
                                {floaterData.planets.map((p, i) => (
                                    <tr key={i} style={{ color: p.retro ? '#d32f2f' : '#222' }}>
                                        <td style={{ padding: '1px 6px' }}>{p.name}</td>
                                        <td style={{ textAlign: 'right', padding: '1px 6px' }}>{p.longitude}°</td>
                                        <td style={{ padding: '1px 6px' }}>{p.sign} {p.degInSign}°</td>
                                        <td style={{ textAlign: 'right', padding: '1px 6px' }}>{p.declination}°</td>
                                        <td style={{ textAlign: 'right', padding: '1px 6px' }}>{p.distance}</td>
                                        <td style={{ textAlign: 'right', padding: '1px 6px' }}>{p.speed}{p.retro ? ' R' : ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    {(barFloaterMode === 'aspects_geo' || barFloaterMode === 'aspects_helio') && floaterData.aspects && (
                        <table style={{ borderCollapse: 'collapse', fontSize: 11 }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid #ccc' }}>
                                    <th style={{ textAlign: 'left', padding: '1px 6px' }}>Planet 1</th>
                                    <th style={{ textAlign: 'left', padding: '1px 6px' }}>Aspect</th>
                                    <th style={{ textAlign: 'left', padding: '1px 6px' }}>Planet 2</th>
                                    <th style={{ textAlign: 'right', padding: '1px 6px' }}>Orb°</th>
                                </tr>
                            </thead>
                            <tbody>
                                {floaterData.aspects.map((a, i) => {
                                    const ASPECT_COLORS = {
                                        'Conjunction': '#1565c0',
                                        'Semi-sextile': '#7b1fa2',
                                        'Sextile': '#00838f',
                                        'Square': '#d84315',
                                        'Trine': '#2e7d32',
                                        'Quincunx': '#6a1b9a',
                                        'Opposition': '#c62828',
                                    };
                                    const MAJOR = ['Conjunction', 'Square', 'Trine', 'Opposition'];
                                    const isMajor = MAJOR.includes(a.aspect);
                                    const clr = ASPECT_COLORS[a.aspect] || '#555';
                                    return (
                                    <tr key={i} style={{ fontWeight: isMajor ? 700 : 400 }}>
                                        <td style={{ padding: '1px 6px', color: clr }}>{a.planet1}</td>
                                        <td style={{ padding: '1px 6px', color: clr }}>
                                            {a.aspect} ({a.angle}°)
                                        </td>
                                        <td style={{ padding: '1px 6px', color: clr }}>{a.planet2}</td>
                                        <td style={{ textAlign: 'right', padding: '1px 6px', color: clr }}>{a.orb}°</td>
                                    </tr>
                                    );
                                })}
                                {floaterData.aspects.length === 0 && (
                                    <tr><td colSpan={4} style={{ padding: '4px 6px', color: '#888' }}>No aspects within orb</td></tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </Box>
            )}
            {/* Text Annotation Overlays */}
            <TextAnnotationLayer
                overlays={overlays}
                chartRef={chartRef}
                onUpdateOverlay={onUpdateOverlay}
            />
        </Box>
    );
});

// --- Text Annotation Layer component ---
function TextAnnotationLayer({ overlays, chartRef, onUpdateOverlay }) {
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState('');

    const textOverlays = useMemo(
        () => (overlays || []).filter(o => o.type === 'textAnnotation'),
        [overlays]
    );

    // Compute pixel positions from data coordinates
    const [positions, setPositions] = useState({});
    const updatePositions = useCallback(() => {
        const instance = chartRef.current?.getEchartsInstance?.();
        if (!instance) return;
        const newPositions = {};
        textOverlays.forEach(overlay => {
            // If the overlay has a pixel offset from dragging, use that
            if (overlay.pixelX != null && overlay.pixelY != null) {
                newPositions[overlay.id] = { x: overlay.pixelX, y: overlay.pixelY };
                return;
            }
            // Otherwise compute from date + price
            try {
                const dates = instance.getOption()?.xAxis?.[0]?.data;
                if (!dates) return;
                const dateStr = overlay.date?.split?.('T')?.[0] || overlay.date;
                const dataIndex = dates.indexOf(dateStr);
                if (dataIndex === -1) return;
                const pixel = instance.convertToPixel({ gridIndex: 0 }, [dataIndex, overlay.price]);
                if (pixel) {
                    newPositions[overlay.id] = { x: pixel[0], y: pixel[1] };
                }
            } catch (_) { /* ignore */ }
        });
        setPositions(newPositions);
    }, [textOverlays, chartRef]);

    // Update positions on render and on zoom/resize
    useEffect(() => {
        updatePositions();
        const instance = chartRef.current?.getEchartsInstance?.();
        if (!instance) return;
        const handler = () => updatePositions();
        instance.on('dataZoom', handler);
        instance.on('restore', handler);
        window.addEventListener('resize', handler);
        return () => {
            try { instance.off('dataZoom', handler); } catch (_) { /* cleanup */ }
            try { instance.off('restore', handler); } catch (_) { /* cleanup */ }
            window.removeEventListener('resize', handler);
        };
    }, [updatePositions, chartRef]);

    // Also update whenever finalChartOptions changes (detected via overlays length change)
    useEffect(() => {
        const timer = setTimeout(updatePositions, 100);
        return () => clearTimeout(timer);
    }, [overlays, updatePositions]);

    const handleDragStart = useCallback((e, overlayId) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startY = e.clientY;
        const startPos = positions[overlayId] || { x: 0, y: 0 };

        const onMove = (me) => {
            const dx = me.clientX - startX;
            const dy = me.clientY - startY;
            setPositions(prev => ({
                ...prev,
                [overlayId]: { x: startPos.x + dx, y: startPos.y + dy }
            }));
        };
        const onUp = (me) => {
            const dx = me.clientX - startX;
            const dy = me.clientY - startY;
            const finalX = startPos.x + dx;
            const finalY = startPos.y + dy;
            if (onUpdateOverlay) {
                onUpdateOverlay(overlayId, { pixelX: finalX, pixelY: finalY });
            }
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    }, [positions, onUpdateOverlay]);

    const handleDoubleClick = useCallback((e, overlay) => {
        e.preventDefault();
        e.stopPropagation();
        setEditingId(overlay.id);
        setEditText(overlay.text || '');
    }, []);

    const commitEdit = useCallback(() => {
        if (editingId && onUpdateOverlay) {
            onUpdateOverlay(editingId, { text: editText });
        }
        setEditingId(null);
    }, [editingId, editText, onUpdateOverlay]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            commitEdit();
        } else if (e.key === 'Escape') {
            setEditingId(null);
        }
    }, [commitEdit]);

    if (textOverlays.length === 0) return null;

    return (
        <>
            {textOverlays.map(overlay => {
                const pos = positions[overlay.id];
                if (!pos) return null;
                const isEditing = editingId === overlay.id;
                // Each note renders at the size it was placed with. Notes created before this
                // existed carry no fontSize, and BASE_TEXT_SIZE is exactly what they were drawn
                // at, so they are unchanged.
                const fontSize = overlay.fontSize || BASE_TEXT_SIZE;

                return (
                    <div
                        key={overlay.id}
                        style={{
                            position: 'absolute',
                            left: pos.x,
                            top: pos.y,
                            transform: 'translate(-50%, -100%)',
                            zIndex: 1500,
                            cursor: isEditing ? 'text' : 'grab',
                            userSelect: 'none',
                            pointerEvents: 'auto',
                        }}
                        onMouseDown={!isEditing ? (e) => handleDragStart(e, overlay.id) : undefined}
                        onDoubleClick={(e) => handleDoubleClick(e, overlay)}
                    >
                        {isEditing ? (
                            <input
                                autoFocus
                                value={editText}
                                onChange={(e) => setEditText(e.target.value)}
                                onBlur={commitEdit}
                                onKeyDown={handleKeyDown}
                                style={{
                                    fontSize,
                                    fontWeight: 'bold',
                                    color: overlay.color || '#7b1fa2',
                                    border: '1px solid #7b1fa2',
                                    borderRadius: 3,
                                    padding: '2px 6px',
                                    outline: 'none',
                                    background: 'rgba(255,255,255,0.95)',
                                    minWidth: 40,
                                }}
                            />
                        ) : (
                            <span
                                style={{
                                    // Editing and display must match, or text jumps size the
                                    // moment you double-click it.
                                    fontSize,
                                    fontWeight: 'bold',
                                    color: overlay.color || '#7b1fa2',
                                    background: 'rgba(255,255,255,0.85)',
                                    borderRadius: 3,
                                    padding: '2px 6px',
                                    border: '1px solid transparent',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {overlay.text || 'Note'}
                            </span>
                        )}
                    </div>
                );
            })}
        </>
    );
}

TextAnnotationLayer.propTypes = {
    overlays: PropTypes.array,
    chartRef: PropTypes.object,
    onUpdateOverlay: PropTypes.func,
};

EChartComponent.propTypes = {
    barData: PropTypes.object.isRequired,
    theme: PropTypes.object.isRequired,
    loading: PropTypes.bool,
    style: PropTypes.object,
    chartType: PropTypes.string,
    viewMode: PropTypes.string,
    chartColors: PropTypes.object,
    overlays: PropTypes.array,
    maConfigs: PropTypes.array,
    showRSI: PropTypes.bool,
    rsiData: PropTypes.array,
    showMACD: PropTypes.bool,
    macdData: PropTypes.object,
    setPatternDialogOpen: PropTypes.func.isRequired,
    onBarSelect: PropTypes.func,
    studyMode: PropTypes.bool,
    barFloaterMode: PropTypes.string,
    watermarkText: PropTypes.string,
    watermarkOverride: PropTypes.shape({
        hidden: PropTypes.bool,
        text: PropTypes.string,
        fontSize: PropTypes.number,
    }),
    onUpdateOverlay: PropTypes.func,
};

EChartComponent.displayName = 'EChartComponent';

export default EChartComponent;